import React, { useEffect, useMemo } from 'react';

import { batch, useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import PriceCheckIcon from '@mui/icons-material/PriceCheck';

import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';


import { useGetDashboardSummaryQuery } from '../../redux/features/dashboardApi';

import { useGetExchangeRatesQuery } from '../../redux/features/exchangeRatesApi';

import { getContacts } from '../../redux/slices/contactSlice';

import { getItems as getBankAccounts } from '../../redux/slices/bankAccountSlice';

import { getItems as getItemsForItemInv } from '../../redux/slices/itemForItemInvSlice';

import { getItems as getNoteTerms } from '../../redux/slices/noteTermSlice';

import { getItems as getContractTerms } from '../../redux/slices/contractTermSlice';

import { getItems as getCurrencies } from '../../redux/slices/currencySlice';

import { getStatuses, setStatusesLocal, setStatusesLocalInv } from '../../redux/slices/statusSlice';

import { setPopup2 } from '../../redux/slices/popupSlice';

import { setItemForEdit } from '../../redux/slices/itemForEditSlice';

import Contact from '../Apm/Contact/Contact';
import UnpaidInvoicesCard from '../Apm/Dashboard/UnpaidInvoicesCard';
import { buildDashboardStats } from '../Apm/Dashboard/dashboardStats';
import BnmRatesCard from '../Apm/Dashboard/BnmRatesCard';
import Inv from '../Apm/Inv/Inv';
import Invoiced30Days from '../Apm/Dashboard/Invoiced30Days';
import Invoiced6Months from '../Apm/Dashboard/Invoiced6Months';

import Popup2 from '../Controls/Popups/Popup2';
import PopupQuote from '../Controls/Popups/PopupQuote';
import PopupInv from '../Controls/Popups/PopupInv';

import Quote from '../Apm/Quote/Quote';
import QuoteIcon from '../Data/Icons/QuoteIcon';

import { statusesInDiffLang } from '../Data/generalData';


const SummaryRow = ({ icon, label, value }) => {
  return (
    <>
      <Grid container item xs={12}>
        <Grid
          item
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              mt: 1,
            }}
          >
            <Box
              sx={{
                width: '65%',
              }}
            >
              <Stack direction="row">
                {icon}

                <Typography
                  variant="body1"
                  sx={{
                    ml: 1,
                    pt: 0.5,
                    pb: 1,
                    fontWeight: 500,
                  }}
                >
                  {label}
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'end',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  pt: 0.5,
                  pr: 1,
                  pb: 1,
                  fontWeight: 500,
                }}
              >
                {value}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Divider />
    </>
  );
};

const Dashboard = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();
  const theme = useTheme();

  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));

  /*
   * RTK Query server state
   */
  const {
    data: summary,
    isFetching: isSummaryFetching,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useGetDashboardSummaryQuery(undefined, {
    pollingInterval: 60 * 1000,
    refetchOnMountOrArgChange: 30,
  });

  const {
    data: exchangeRates,
    isFetching: areRatesFetching,
    isError: areRatesError,
    refetch: refetchRates,
  } = useGetExchangeRatesQuery(undefined, {
    pollingInterval: 15 * 60 * 1000,
    refetchOnMountOrArgChange: 300,
  });

  /*
   * UI state only
   */
  const { popup2, popupQuote, popupInv, selectedInv, selectedQuote } = useSelector((state) => state.popupSlice);

  const { itemForEdit } = useSelector((state) => state.itemForEditSlice);

  /*
   * Legacy lookup state.
   * These can be migrated to RTK Query separately.
   */
  const { contacts, statusContacts } = useSelector((state) => state.contactSlice);

  const { bankAccounts, statusBankAccounts } = useSelector((state) => state.bankAccountSlice);

  const { itemsForItemInv, statusItemsForItemInv } = useSelector((state) => state.itemForItemInvSlice);

  const { noteTerms, statusNoteTerms } = useSelector((state) => state.noteTermSlice);

  const { contractTerms, statusContractTerms } = useSelector((state) => state.contractTermSlice);

  const { currencies, statusCurrencies } = useSelector((state) => state.currencySlice);

  const { statuses, statusStatuses } = useSelector((state) => state.statusSlice);

  const company = summary?.company ?? null;

  /*
   * Temporary lookup loading.
   *
   * Remove this effect after these resources get their
   * own RTK Query endpoints.
   */
  useEffect(() => {
    batch(() => {
      if (contacts.length < 1 && statusContacts === null) {
        dispatch(getContacts('/'));
      }

      if (bankAccounts.length < 1 && statusBankAccounts === null) {
        dispatch(getBankAccounts('/'));
      }

      if (itemsForItemInv.length < 1 && statusItemsForItemInv === null) {
        dispatch(getItemsForItemInv('/'));
      }

      if (noteTerms.length < 1 && statusNoteTerms === null) {
        dispatch(getNoteTerms('/'));
      }

      if (contractTerms.length < 1 && statusContractTerms === null) {
        dispatch(getContractTerms('/'));
      }

      if (statuses.length < 1 && statusStatuses === null) {
        dispatch(getStatuses());
      }

      if (currencies.length < 1 && statusCurrencies === null) {
        dispatch(getCurrencies('/'));
      }
    });
  }, [
    dispatch,
    contacts.length,
    statusContacts,
    bankAccounts.length,
    statusBankAccounts,
    itemsForItemInv.length,
    statusItemsForItemInv,
    noteTerms.length,
    statusNoteTerms,
    contractTerms.length,
    statusContractTerms,
    statuses.length,
    statusStatuses,
    currencies.length,
    statusCurrencies,
  ]);

  /*
   * Keep localized statuses in the legacy status slice until
   * SelectBasic and related forms are migrated.
   */
  useEffect(() => {
    if (!statuses.length) {
      return;
    }

    const language = (i18n.resolvedLanguage || i18n.language || 'ro').split('-')[0];

    const labels = statusesInDiffLang();

    const quoteLabelRanges = {
      en: labels.slice(0, 4),
      ro: labels.slice(4, 8),
      ru: labels.slice(8, 12),
    };

    const invoiceLabelRanges = {
      en: labels.slice(12, 17),
      ro: labels.slice(17, 22),
      ru: labels.slice(22, 27),
    };

    const quoteStatuses = statuses.filter(
      (status) => Number(status.serial_number) >= 100 && Number(status.serial_number) < 200
    );

    const invoiceStatuses = statuses.filter((status) => Number(status.serial_number) >= 200);

    dispatch(
      setStatusesLocal(
        quoteStatuses.map((status, index) => ({
          ...status,
          type: quoteLabelRanges[language]?.[index] ?? '',
        }))
      )
    );

    dispatch(
      setStatusesLocalInv(
        invoiceStatuses.map((status, index) => ({
          ...status,
          type: invoiceLabelRanges[language]?.[index] ?? '',
        }))
      )
    );
  }, [dispatch, i18n.language, i18n.resolvedLanguage, statuses]);

  const dashboardStats = useMemo(
    () => buildDashboardStats(summary, exchangeRates, i18n.resolvedLanguage || i18n.language || 'ro'),
    [summary, exchangeRates, i18n.language, i18n.resolvedLanguage]
  );
  const quotesIssuedThisMonth = summary?.counts.quotes_this_month ?? '—';
  const invoicesIssuedThisMonth = summary?.counts.invoices_this_month ?? '—';
  const yearToDateTotal = dashboardStats?.yearToDate;
  const isDashboardFetching = isSummaryFetching || areRatesFetching;

  const addOrEditClient = () => {
    batch(() => {
      dispatch(setPopup2(false));
      dispatch(setItemForEdit(null));
    });
  };

  return (
    <>
      {isDashboardFetching && <LinearProgress />}
      {isSummaryError && (
        <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" onClick={refetchSummary}>{t('dashboard.retry')}</Button>}>
          {t('dashboard.load_error')}
        </Alert>
      )}
      {dashboardStats?.missingRates && (
        <Alert severity="warning" sx={{ m: 2 }}>{t('dashboard.missing_exchange_rates')}</Alert>
      )}

      <Grid
        container
        item
        spacing={2}
        sx={{
          mt: 0,
          height: '100%',
        }}
      >
        <Grid item lg={4} md={4} xl={4} xs={12}>
          <UnpaidInvoicesCard summary={summary} isLoading={isSummaryFetching} isError={isSummaryError} />
        </Grid>

        <Grid item lg={4} md={4} xl={4} xs={12}>
          <Card
            sx={{
              height: 520,
              maxWidth: 'auto',
            }}
          >
            <CardHeader
              title={t('dashboard.sales')}
              subheader={company?.companyname || ' '}
              sx={{
                backgroundColor: '#f1f1f1',
                minHeight: 85,
              }}
            />

            <Divider />

            <CardContent>
              <SummaryRow
                icon={<QuoteIcon fill="#757575" />}
                label={t('dashboard.quotes_issued_this_month_colon')}
                value={quotesIssuedThisMonth}
              />

              <SummaryRow
                icon={<ReceiptLongIcon />}
                label={t('dashboard.invoices_issued_this_month_colon')}
                value={invoicesIssuedThisMonth}
              />

              <SummaryRow icon={<PeopleAltIcon />} label={t('dashboard.clients_colon')} value={summary?.counts.clients ?? '—'} />

              <SummaryRow
                icon={<PriceCheckIcon />}
                label={t('dashboard.goods_services_colon')}
                value={summary?.counts.items ?? '—'}
              />

              <SummaryRow icon={<PeopleOutlineIcon />} label={t('dashboard.users_colon')} value={summary?.counts.users ?? '—'} />

              <SummaryRow
                icon={<AttachMoneyIcon />}
                label={t('dashboard.total_invoiced_this_year')}
                value={
                  Number.isFinite(yearToDateTotal)
                    ? `${yearToDateTotal.toLocaleString(i18n.resolvedLanguage || i18n.language || 'ro', {
                        maximumFractionDigits: 0,
                      })} MDL`
                    : '—'
                }
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item lg={4} md={4} xl={4} xs={12}>
          <BnmRatesCard
            exchangeRates={exchangeRates}
            isFetching={areRatesFetching}
            isError={areRatesError}
            onRetry={refetchRates}
          />
        </Grid>

        <Grid item xs={12} md={12} lg={6} xl={6}>
          <Invoiced6Months stats={dashboardStats} isLoading={isSummaryFetching} isError={isSummaryError} />
        </Grid>

        <Grid item xs={12} md={12} lg={6} xl={6}>
          <Invoiced30Days stats={dashboardStats} isLoading={isSummaryFetching} isError={isSummaryError} />
        </Grid>
      </Grid>

      {popupQuote && selectedQuote && (
        <PopupQuote
          title={`${t(smMediaDown ? 'quote.quote_hash_short' : 'quote.quote_hash')} ${selectedQuote.qn || ''}`}
        >
          <Quote initialValue={selectedQuote} />
        </PopupQuote>
      )}

      {popupInv && selectedInv && (
        <PopupInv title={`${t(smMediaDown ? 'inv.invoice_hash_short' : 'inv.invoice_hash')} ${selectedInv.vn || ''}`}>
          <Inv initialValue={selectedInv} />
        </PopupInv>
      )}

      {popup2 && itemForEdit?.fieldName === 'bill_to' && (
        <Popup2 title={t('popup.title.client_details')}>
          <Contact addOrEdit={addOrEditClient} />
        </Popup2>
      )}
    </>
  );
};

export default Dashboard;
